import { useMemo } from 'react';
import { useWindowSize } from './useWindowSize';
import { useAtomValue } from 'jotai';
import * as analysisState from '@/components/Analysis/state';

// Screen size breakpoints
const BREAKPOINTS = {
  SMALL: 1280,
  MEDIUM: 1728,
};

// Base drawer width configurations
const DRAWER_WIDTHS_CONFIG = {
  [BREAKPOINTS.SMALL]: {
    leftDrawerWidth: 220,
    doublePanelWidth: 540,
    riskDrawerWidth: 220,
  },
  [BREAKPOINTS.MEDIUM]: {
    leftDrawerWidth: 230,
    doublePanelWidth: 580,
    riskDrawerWidth: 230,
  },
  DEFAULT: {
    leftDrawerWidth: 280,
    doublePanelWidth: 828,
    riskDrawerWidth: 280,
  },
};

// Right drawer width configurations (using wider widths for all cases)
const RIGHT_DRAWER_WIDTHS = {
  [BREAKPOINTS.SMALL]: 300,
  [BREAKPOINTS.MEDIUM]: 320,
  DEFAULT: 380,
};

export const useDrawerWidths = () => {
  const sideBySideModeEnabled = useAtomValue(analysisState.sideBySideModeEnabled);
  const drawerRight = useAtomValue(analysisState.drawerRight);
  const screenSize = useWindowSize();

  const halfScreenWidth = useMemo(() => {
    const { width } = screenSize;
    return width ? width / 2 : 0;
  }, [screenSize]);

  const drawerWidths = useMemo(() => {
    const { width } = screenSize;

    // Determine which configuration to use based on screen width
    let config;
    if (width && width <= BREAKPOINTS.SMALL) {
      config = DRAWER_WIDTHS_CONFIG[BREAKPOINTS.SMALL];
    } else if (width && width <= BREAKPOINTS.MEDIUM) {
      config = DRAWER_WIDTHS_CONFIG[BREAKPOINTS.MEDIUM];
    } else {
      config = DRAWER_WIDTHS_CONFIG.DEFAULT;
    }

    // Determine right drawer width based on mode and screen size
    // Always use wider widths (formerly REVIEWER widths) for all cases
    let rightDrawerWidth;
    if (sideBySideModeEnabled && drawerRight.equipment && !drawerRight.measuringTool) {
      rightDrawerWidth = halfScreenWidth;
    } else if (width && width <= BREAKPOINTS.SMALL) {
      rightDrawerWidth = RIGHT_DRAWER_WIDTHS[BREAKPOINTS.SMALL];
    } else if (width && width <= BREAKPOINTS.MEDIUM) {
      rightDrawerWidth = RIGHT_DRAWER_WIDTHS[BREAKPOINTS.MEDIUM];
    } else {
      rightDrawerWidth = RIGHT_DRAWER_WIDTHS.DEFAULT;
    }

    return {
      ...config,
      rightDrawerWidth,
    };
  }, [
    screenSize,
    sideBySideModeEnabled,
    drawerRight.equipment,
    drawerRight.measuringTool,
    halfScreenWidth,
  ]);

  return drawerWidths;
};
